import {
	BulkAddCartItems,
	CartErrorType,
	CartItem,
	LineItem,
	PhysicalItem,
} from "types/CartTypes";
import {
	clearCart,
	setCartError,
	setCartIsLoading,
	setLastAddedItem,
	updateCart,
} from "redux/cart/cartSlice";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
	useAddCartItemMutation,
	useCreateCartMutation,
	useDeleteCartItemMutation,
	useDeleteCartMutation,
	useLazyGetCartQuery,
	useUpdateCartCustomerMutation,
	useUpdateCartItemMutation,
} from "app/api/cart/cartApi";
import { useDispatch, useSelector } from "redux/hooks";

import { COOKIE_CART_ID } from "utilities/consts";
import { PageType } from "ts/enums";
import { Product } from "ts/types";
import { setShowAddToCartConformation } from "redux/UI/uiSlice";
import useDataLayer from "datalayer/useDataLayer";
import { useLazyGetProductBySkuQuery } from "app/api/product/productApi";
import { addItemToCookie } from "components/pdp/PdpDetails/PdpSizeActions/PdpSizeGuide/Measurement/UnitsOfMeasure";
import { getCartCookieExpirationDate } from "behavior/getCartCookieExpiration";
import { deleteAppCookie, getAppCookie, setAppCookie } from "utilities/cookies";
import { useLazyGetCartFromDBQuery } from "app/api/cart/cartApi";

declare global {
	interface Window {
		/**
		 * Segmentify Add Cart Item
		 * @param params Segmentify Add Cart Item Parameters
		 */
		segmentifyAddCartItem(params: SegmentifyAddCartItemOperation): Promise<any>;
	}

	export type SegmentifyAddCartItemOperation = {
		sku: string;
		variant: number | undefined;
		quantity: number;
	};
}

export type CartActionOptions = {
	triggeredFrom?: string;
	onSuccess?: () => void;
	onError?: (error: any) => void;
};

type CartCookieData = {
	storeHash: string;
	id: string;
	customer: number;
};

type CartContextType = {
	customerId: number;
	storeHash: undefined | string;
	isLoading: boolean;
	fetchCart: (shouldDeleteCart?: boolean) => void;
	internalDeleteCart: (skipApi?: boolean, storeHash?: string) => Promise<void>;
	setLoading: (loading: boolean) => void;
	unavailableItemIds: Set<string>;
	hasUnavailableItems: boolean;
	isCheckingAvailability: boolean;
};

const CartContext = createContext({} as CartContextType);

export const CartProvider = ({ children }: { children: any }) => {
	const [getCartQueryTrigger] = useLazyGetCartQuery();
	const [getCartFromDBTrigger] = useLazyGetCartFromDBQuery();
	const [updateCartCustomer] = useUpdateCartCustomerMutation();
	const [deleteCartMutation] = useDeleteCartMutation();
	const [updateCartItemForAvailability] = useUpdateCartItemMutation();

	const dispatch = useDispatch();

	const { auth, cart, bcStore } = useSelector((state) => state);

	const [isLoading, setIsLoading] = useState(cart.isLoading);

	const [unavailableItemIds, setUnavailableItemIds] = useState<Set<string>>(
		new Set(),
	);
	const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

	const availabilityItemsKey = useMemo(() => {
		const items = cart.cart?.line_items?.physical_items ?? [];
		return items
			.map((item) => `${item.id}:${item.product_id}:${item.quantity}`)
			.sort()
			.join("|");
	}, [cart.cart]);

	useEffect(() => {
		const items = cart.cart?.line_items?.physical_items ?? [];

		if (!cart.cart?.id || items.length === 0) {
			setUnavailableItemIds(new Set());
			return;
		}

		// drop ids that are no longer in the cart (e.g. just removed) immediately,
		// instead of waiting for the next probe round-trip to confirm
		const currentItemIds = new Set(items.map((item) => item.id));
		setUnavailableItemIds((prev) => {
			const pruned = new Set(
				Array.from(prev).filter((id) => currentItemIds.has(id)),
			);
			return pruned.size === prev.size ? prev : pruned;
		});

		let cancelled = false;
		setIsCheckingAvailability(true);

		Promise.all(
			items.map((item) =>
				updateCartItemForAvailability({
					cartId: cart.cart?.id,
					itemId: item.id,
					newItem: {
						product_id: item.product_id,
						quantity: item.quantity,
					},
				})
					.unwrap()
					.then(() => null)
					.catch((error: any) => (error?.status === 422 ? item.id : null)),
			),
		).then((results) => {
			if (cancelled) return;

			setUnavailableItemIds(new Set(results.filter(Boolean) as string[]));
			setIsCheckingAvailability(false);
		});

		return () => {
			cancelled = true;
		};
	}, [availabilityItemsKey]);

	const customerId = useMemo(() => {
		const id = auth?.customer?.id;
		return id;
	}, [auth?.customer?.id]);

	const prevCustomerIdRef = useRef(customerId);

useEffect(() => {
  const prevCustomerId = prevCustomerIdRef.current;
  prevCustomerIdRef.current = customerId;

  const cartCustomerId = cart.cart?.customer_id;

  if (
    cartCustomerId === 0 &&
    customerId !== undefined &&
    cartCustomerId !== customerId
  ) {
    updateCartCustomer({ cartId: cart.cart?.id })
      .unwrap()
      .then(async (updatedCart) => {
        const cookieData = {
          storeHash: bcStore.store?.hash,
          id: updatedCart.id,
          customer: updatedCart.customer_id,
        } as CartCookieData;

        await setAppCookie(
          COOKIE_CART_ID,
          cookieData,
          getCartCookieExpirationDate,
          customerId
        );

        dispatch(updateCart(updatedCart));

      })
      .catch(async (error) => {
        // 403 means cart is already assigned to this customer (signed in via BC checkout)
        // Fetch the customer's saved cart instead of clearing
        if (error?.status === 403 || error?.data?.error?.status === 403) {
          if (customerId) {
            const customerCart = await getCartFromDBTrigger(customerId).unwrap().catch(() => null);
            if (customerCart) {
              dispatch(updateCart(customerCart));
              return;
            }
          }
        }
        dispatch(clearCart());
        await deleteAppCookie(COOKIE_CART_ID);
      });
  }

  // Only clear the cart on actual logout: customerId transitioned from a known
  // value → undefined. This prevents clearing the cart when BC checkout returns
  // a cart with a non-zero customer_id while the user is not yet authenticated
  // in the app (e.g. after "Izmijeni" redirect with ?cartId param).
  if (!customerId && prevCustomerId !== undefined && cartCustomerId && cartCustomerId !== 0) {
    dispatch(clearCart());
    deleteAppCookie(COOKIE_CART_ID);
  }
}, [customerId, cart.cart?.customer_id]);

useEffect(() => {
  const loadCartForLoggedUser = async () => {
    if (!customerId) return;

    try {
      const cookieData = await getAppCookie(COOKIE_CART_ID);

      if (cookieData?.id && !cart.cart) {
        const fetchedCart = await getCartQueryTrigger(cookieData.id).unwrap();
        dispatch(updateCart(fetchedCart));
      }
    } catch (err) {
      console.error("Failed to fetch cart for logged user:", err);
    }
  };

  loadCartForLoggedUser();
}, [customerId]);

	const fetchCart = async (shouldDeleteCart = false) => {
		if (shouldDeleteCart) {
			await internalDeleteCart();


			window.location.href = "/";
		}

		if (bcStore.store) {
			try {
				const urlParams = new URLSearchParams(window.location.search);
				const urlCartId = urlParams.get('cartId');

				const cookieData = await getAppCookie(COOKIE_CART_ID);
				const cookieCartId = cookieData?.id;

				// Prefer ?cartId URL param (BC checkout "Izmijeni" redirect) over cookie.
				// Cookie may still point to the old guest cart when BC checkout login
				// creates/reassigns a cart under a different ID.
				const cartIdToFetch = urlCartId || cookieCartId;

				if (!cartIdToFetch) return;

				// Apply auth/store checks only when loading from cookie (no URL override).
				// When ?cartId is present we trust BC checkout's redirect and skip these guards.
				if (!urlCartId && cookieCartId) {
					const isGuestCart = cookieData.customer === 0;
					if (!isGuestCart && customerId === undefined) return;

					if (cookieData.storeHash && bcStore.store.hash !== cookieData.storeHash) return;
				}

				getCartQueryTrigger(cartIdToFetch)
					.unwrap()
					.then(async (fetchedCart) => {
						// If we fetched via URL param, update the cookie so subsequent loads
						// work without the URL param and have the correct customer_id.
						if (urlCartId) {
							await setAppCookie(
								COOKIE_CART_ID,
								{ id: fetchedCart.id, customer: fetchedCart.customer_id ?? 0, storeHash: bcStore.store!.hash },
								getCartCookieExpirationDate,
							);
						}
						dispatch(updateCart(fetchedCart));
					})
					.catch((error) => {
						if (error?.status === 400 || error?.status === 401) {
							// Cart was taken over at BC checkout — do NOT delete from BC.
							// Backend fix handles this case; this is a safety net only.
							deleteAppCookie(COOKIE_CART_ID);
							dispatch(updateCart(undefined));
						} else if (error?.status === 404) {
							internalDeleteCart();
						}
						// Ignore infrastructure errors (425 auth-not-ready, network failures, etc.)
						// to avoid deleting the cookie for transient issues.
					});
			} catch (err) {
				console.error("Failed to get cart cookie:", err);
			}
		}
	};


	useEffect(() => {
		const urlCartId = new URLSearchParams(window.location.search).get('cartId');
		if (bcStore.store && auth.authInitialized && (!cart.cart || urlCartId)) {
			fetchCart();
		}
	}, [bcStore.store, auth.authInitialized]);


	const internalDeleteCart = async (
		skipApi: boolean = false,
		storeHash?: string,
	) => {
		await deleteAppCookie(COOKIE_CART_ID);

		if (!cart.cart?.id) {
			return;
		}

		if (!skipApi) {
			await deleteCartMutation({ cartId: cart.cart?.id });
		}

		dispatch(updateCart(undefined));

		return;
	};

	const setLoading = (loading: boolean) => {
		// if (loading !== isLoading) {
		setIsLoading(loading);
		dispatch(setCartIsLoading(loading));
		// }
	};

	return (
		<CartContext.Provider
			value={{
				customerId: customerId || 0,
				storeHash: bcStore?.store?.hash,
				fetchCart,
				internalDeleteCart,
				setLoading,
				isLoading,
				unavailableItemIds,
				hasUnavailableItems: unavailableItemIds.size > 0,
				isCheckingAvailability,
			}}
		>
			{children}
		</CartContext.Provider>
	);
};

const useCart = () => {
	const {
		customerId,
		storeHash,
		isLoading,
		internalDeleteCart,
		fetchCart,
		setLoading,
		unavailableItemIds,
		hasUnavailableItems,
		isCheckingAvailability,
	} = useContext<CartContextType>(CartContext);

	const [createCartMutation] = useCreateCartMutation();
	const [addCartItemMutation] = useAddCartItemMutation();
	const [updateCartItemMutation] = useUpdateCartItemMutation();
	const [deleteCartItemMutation] = useDeleteCartItemMutation();

	const datalayer = useDataLayer();
	const dispatch = useDispatch();

	const { cart } = useSelector((state) => state);
	const bcStore = useSelector((state) => state.bcStore.store);

	const [getProduct] = useLazyGetProductBySkuQuery();

	const fireDataLayerEvents = async (
		triggeredFrom: string,
		type: "add" | "remove",
		currencyCode: string,
		item: PhysicalItem & {
			parent_sku: string;
		},
		quantityChange: number,
		product?: Product,
	) => {
		if (!item || !cart) {
			return;
		}

		const breadcrumbs = product?.categories?.breadcrumbs || [];

		const id = breadcrumbs.length
			? breadcrumbs[breadcrumbs.length - 1].entityId.toString()
			: "";

		const name = breadcrumbs.length
			? breadcrumbs[breadcrumbs.length - 1].name
			: "";

		if (type === "add") {
			datalayer.addToCart(triggeredFrom, item, quantityChange, currencyCode, {
				id,
				name,
			});
		} else {
			datalayer.removeFromCart(
				triggeredFrom,
				item,
				quantityChange,
				currencyCode,
				{
					id,
					name,
				},
			);
		}
	};

	const handleCartError = (error: any) => {
		const cartError = {
			code: error.status,
			type: "generic",
			translationKey: error?.data?.error?.message || "cart.error.unknown",
			message: error?.data?.error?.message
				? undefined
				: "A unknown error occurred",
		} as CartErrorType;

		switch (cartError.code) {
			case 422:
				cartError.type = "insufficient_stock";
				cartError.translationKey = "cart.error.insufficientStock";
				cartError.message = error.data.error.message;
				break;
		}
		dispatch(setCartError(cartError));
	};
	const createCart = async (
		lineItems?: CartItem[],
		options?: CartActionOptions,
	) => {
		try {
			const cart = await createCartMutation({
				...(bcStore?.embeddedCheckoutChannelId && {
					channelId: bcStore.embeddedCheckoutChannelId,
				}),
				...(customerId && {
					customerId,
				}),
				lineItems: lineItems ?? [],
			})
				.unwrap()
				.then((res) => res)
				.catch((error) => {
					options?.onError?.(error);
					handleCartError(error);
					return;
				});

			if (!cart) return;

			const cookieData = {
				storeHash,
				id: cart.id,
				customer: cart.customer_id,
			} as CartCookieData;

			await setAppCookie(
				COOKIE_CART_ID,
				cookieData,
				getCartCookieExpirationDate,
				customerId
			);

			dispatch(updateCart(cart));
			return cart;
		} catch {
			return undefined;
		}
	};

	const deleteCart = async (storeHash?: string) => {
		setLoading(true);

		await internalDeleteCart(false, storeHash);

		setLoading(false);
	};

	const cartExists = async (): Promise<boolean> => {
		try {
			const cookieData = await getAppCookie(COOKIE_CART_ID);
			if (!cookieData) return false;

			return !!cookieData?.id;
		} catch (e) {
			console.error("Failed to check cart cookie:", e);
			return false;
		}
	};


	const addCartItem = async (
		product: Product,
		variantId: number | undefined,
		quantity: number,
		options?: CartActionOptions,
	) => {
		setLoading(true);

		const groupIDField = product?.customFields?.find(
			(field) => field.name === "GroupID",
		);

		const groupID = groupIDField ? groupIDField.value : null;

		type CookieData = {
			sku: string;
			groupId: string;
		};

		const cookieData: CookieData = {
			sku: product.sku,
			groupId: groupID || "",
		};

		addItemToCookie(cookieData, customerId);

		const lineItems = [convertToLineItem(product, variantId, quantity)];

		let action;

		if (!cart.cart?.id) {
			action = createCart(lineItems);
		} else {
			action = addCartItemMutation({
				id: cart.cart.id,
				lineItems,
			}).unwrap();
		}

		const updatedCart = await action
			.then((cart) => cart)
			.catch((error) => {
				options?.onError?.(error);

				if (error.status === 422) {
					handleCartError(error);
					return;
				}

				internalDeleteCart(true);
				return;
				// this can give e 403 error
			});

		setLoading(false);

		if (!updatedCart) {
			return;
		}

		dispatch(updateCart(updatedCart));

		const matchingItem = getMatchingLineItem(
			product.entityId,
			variantId,
			updatedCart?.line_items.physical_items,
		);

		if (matchingItem) {
			fireDataLayerEvents(
				options?.triggeredFrom || PageType.CART,
				"add",
				updatedCart.currency.code,
				{
					...matchingItem,
					parent_sku: product.sku,
				},
				quantity,
				product,
			);
			options?.onSuccess?.();
			dispatch(setLastAddedItem(matchingItem));
			dispatch(setShowAddToCartConformation(true));
		}
	};

	const bulkAddCartItems = async ({ lineItems, options }: BulkAddCartItems) => {
		setLoading(true);

		let action;

		if (!cart.cart?.id) {
			action = createCart(lineItems);
		} else {
			action = addCartItemMutation({
				id: cart.cart.id,
				lineItems,
			}).unwrap();
		}

		const updatedCart = await action
			.then((cart) => cart)
			.catch((error) => {
				options?.onError?.(error);

				if (error.status === 422) {
					handleCartError(error);
					return;
				}

				internalDeleteCart(true);
				return;
				// this can give e 403 error
			});

		setLoading(false);

		if (!updatedCart) {
			return;
		}

		dispatch(updateCart(updatedCart));

		lineItems.forEach((item) => {
			const matchingItem = getMatchingLineItem(
				item.product_id,
				item.variant_id,
				updatedCart?.line_items.physical_items,
			);

			if (matchingItem) {
				fireDataLayerEvents(
					options?.triggeredFrom || PageType.CART,
					"add",
					updatedCart.currency.code,
					{
						...matchingItem,
						parent_sku: /[0-9](.+)/.exec(matchingItem.name)?.[1] ?? "",
					},
					item.quantity,
					undefined,
				);
			}
		});

		options?.onSuccess?.();
	};

	/**
	 * Update cart item quantity, will remove if quantity is 0.
	 * @param itemId
	 * @param product
	 * @param quantity
	 * @param changeType whether the update was an add or remove, used to determine the correct
	 * datalayer event to fires
	 * @param options
	 * @returns
	 */
	const updateCartItem = async (
		itemId: string,
		product: any,
		quantity: number,
		changeType: "add" | "remove",
		options?: CartActionOptions,
	) => {
		if (!cart.cart?.id) {
			return;
		}
		setLoading(true);

		const cartItem =
			cart.cart.line_items.physical_items.find((item) => item.id === itemId) ||
			undefined;

		// if there isn't a matching item on the cart, don't perform action
		if (!cartItem) {
			setLoading(true);
			return;
		}

		// if quantity is less than 1, do a remove instead
		// this will handle callbacks and datalayer events
		if (quantity < 1) {
			removeCartItem(itemId, options);
			return;
		}

		const updatedCart = await updateCartItemMutation({
			cartId: cart.cart.id,
			itemId,
			newItem: {
				product_id: product?.entityId || product,
				quantity,
			},
		})
			.unwrap()
			.catch((error) => {
				options?.onError?.(error);
				handleCartError(error);
				return;
			});

		if (!updatedCart) {
			setLoading(false);
			return;
		}

		const quantityChange = Math.abs(cartItem.quantity - quantity);

		dispatch(updateCart(updatedCart));
		setLoading(false);

		if (cartItem) {
			fireDataLayerEvents(
				options?.triggeredFrom || PageType.CART,
				changeType,
				updatedCart.currency.code,
				{
					...cartItem,
					parent_sku: /[0-9](.+)/.exec(cartItem.name)?.[1] ?? "",
				},
				quantityChange,
			);
		}

		options?.onSuccess?.();
	};

	const removeCartItem = async (
		itemId: string,
		options?: CartActionOptions,
	) => {
		if (!itemId.length || !cart.cart?.id) {
			return;
		}
		setLoading(true);

		const cartItem =
			cart.cart.line_items.physical_items.find((item) => item.id === itemId) ||
			undefined;

		if (!cartItem) {
			setLoading(false);
			return;
		}

		// get the currency code from the cart now, as this might get deleted
		// need for the datalayer
		const currencyCode = cart.cart.currency.code;

		let action;

		if (cart.totalItems === 1 || cartItem.quantity === cart.totalItems) {
			action = internalDeleteCart();
		} else {
			action = deleteCartItemMutation({
				cartId: cart.cart.id,
				itemId,
			}).unwrap();
		}
		//NOTE - This might need some work on the error side, as this will
		// currently delete the cart?

		const updatedCart = await action
			.then((cart: any) => cart)
			.catch((error: any) => {
				options?.onError?.(error);
			});

		dispatch(updateCart(updatedCart));

		setLoading(false);

		if (cartItem) {
			fireDataLayerEvents(
				options?.triggeredFrom || PageType.CART,
				"remove",
				currencyCode,
				{
					...cartItem,
					parent_sku: /[0-9](.+)/.exec(cartItem.name)?.[1] ?? "",
				},
				cartItem.quantity,
			);
		}

		options?.onSuccess?.();
	};

	const segmentifyAddCartItem = async ({
		sku,
		variant,
		quantity,
	}: SegmentifyAddCartItemOperation) => {
		console.debug("Segmentify attempting to add product to cart", {
			sku,
			variant,
			quantity,
		});

		const product = await getProduct(
			variant ? `${sku}/${variant}` : sku,
		).unwrap();

		console.debug("Product found. Attempting to add product to cart", {
			sku,
			product,
			variant,
			quantity,
		});

		if (!product) {
			console.error("Product not found", { sku });

			return;
		}

		if (variant && !((product.productOptions?.length ?? 0) > 0)) {
			console.error(
				"Segmentify specified a variant while no such variant exists on BC",
				{ sku, variant },
			);

			return;
		}

		const bcVariant = product.variants?.find(
			(v) => v.option.label === String(variant),
		);

		if (variant && !bcVariant) {
			console.error(
				"Segmentify specified a variant that does not exist on BC",
				{ sku, variant },
			);

			return;
		}

		console.debug("Segmentify adding product to cart", {
			sku,
			product,
			variant,
			quantity,
		});

		addCartItem(product, bcVariant ? bcVariant.entityId : undefined, quantity);
	};

	// Expose segmentifyAddCartItem to window
	window.segmentifyAddCartItem = segmentifyAddCartItem;

	return {
		cart: cart.cart,
		meta: {
			isLoading,
			totalItems: cart.totalItems,
			unavailableItemIds,
			hasUnavailableItems,
			isCheckingAvailability,
		},

		createCart,
		deleteCart,
		addCartItem,
		fetchCart,
		bulkAddCartItems,
		updateCartItem,
		removeCartItem,
		cartExists,
	};
};

const convertToLineItem = (
	product: Product,
	variantId: number | undefined,
	quantity: number,
) => {
	return {
		product_id: product.entityId,
		variant_id: variantId,
		quantity: quantity || 1,
	} as LineItem;
};

const getMatchingLineItem = (
	productId: number,
	variantId: number | undefined,
	physicalItems: PhysicalItem[],
) => {
	return physicalItems.find((item) => {
		if (!variantId) {
			return item.product_id === productId;
		} else {
			return item.product_id === productId && item.variant_id === variantId;
		}
	});
};

export default useCart;
